/**
 * Current weather at many points at once, for the forecast board.
 *
 * Open-Meteo answers several coordinates in one request, so a whole map of
 * cities is one round trip. Results are cached per point for fifteen minutes,
 * which is Open-Meteo's own update interval.
 */

import type { Weather } from '../core/weather/weather.js';
import { fromOpenMeteo, OPEN_METEO_VARIABLES } from '../core/weather/weather.js';

const TTL = 15 * 60_000;
const BATCH = 50;

export class WeatherSamples {
  private readonly cache = new Map<string, { weather: Weather; at: number }>();
  private readonly pending = new Set<string>();
  private queued: Array<{ lat: number; lon: number; key: string }> = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  version = 0;

  static key(lat: number, lon: number): string {
    return `${lat.toFixed(2)},${lon.toFixed(2)}`;
  }

  get(lat: number, lon: number): Weather | undefined {
    return this.cache.get(WeatherSamples.key(lat, lon))?.weather;
  }

  /** Ask for these points. Requests are batched and debounced. */
  want(points: Array<{ lat: number; lon: number }>): void {
    const now = performance.now();
    for (const p of points) {
      const key = WeatherSamples.key(p.lat, p.lon);
      const hit = this.cache.get(key);
      if ((hit !== undefined && now - hit.at < TTL) || this.pending.has(key)) continue;
      if (this.queued.some(q => q.key === key)) continue;
      this.queued.push({ lat: p.lat, lon: p.lon, key });
    }
    if (this.queued.length > 0 && this.timer === undefined) {
      this.timer = setTimeout(() => this.flush(), 450);
    }
  }

  private flush(): void {
    this.timer = undefined;
    const batch = this.queued.splice(0, BATCH);
    if (batch.length === 0) return;
    batch.forEach(b => this.pending.add(b.key));
    const url = 'https://api.open-meteo.com/v1/forecast'
      + `?latitude=${batch.map(b => b.lat.toFixed(2)).join(',')}`
      + `&longitude=${batch.map(b => b.lon.toFixed(2)).join(',')}`
      + `&current=${OPEN_METEO_VARIABLES.join(',')}&wind_speed_unit=ms&timezone=UTC`;
    fetch(url)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json: unknown) => {
        const list = Array.isArray(json) ? json : [json];
        list.forEach((entry, i) => {
          const b = batch[i];
          if (b !== undefined) this.cache.set(b.key, { weather: fromOpenMeteo(entry), at: performance.now() });
        });
        this.version++;
      })
      .catch(() => { /* try again on the next want */ })
      .finally(() => {
        batch.forEach(b => this.pending.delete(b.key));
        if (this.queued.length > 0) this.timer = setTimeout(() => this.flush(), 200);
      });
  }
}
